import { useState } from 'react'
import { useAdminUsageStats } from '@/hooks/useAdminUsageStats'
import { format } from 'date-fns'
import UsageGrowthChart from '@/components/UsageGrowthChart'

const PERIOD_DAYS = [7, 30, 90] as const
type ViewMode = 'table' | 'chart'

export const USAGE_METRIC_IDS = [
  'users',
  'groups',
  'threads',
  'comments',
  'articles',
  'articleReads',
  'linkFollows',
] as const
export type UsageMetricId = (typeof USAGE_METRIC_IDS)[number]

export const USAGE_METRIC_LABELS: Record<UsageMetricId, string> = {
  users: 'New users',
  groups: 'New groups',
  threads: 'New threads',
  comments: 'New comments',
  articles: 'New articles',
  articleReads: 'Articles read',
  linkFollows: 'Links followed',
}

const USAGE_METRIC_ROW_KEYS: Record<UsageMetricId, 'new_users' | 'new_groups' | 'new_threads' | 'new_comments' | 'new_articles' | 'article_reads' | 'link_follows'> = {
  users: 'new_users',
  groups: 'new_groups',
  threads: 'new_threads',
  comments: 'new_comments',
  articles: 'new_articles',
  articleReads: 'article_reads',
  linkFollows: 'link_follows',
}

const DEFAULT_VISIBLE_METRICS: Record<UsageMetricId, boolean> = Object.fromEntries(
  USAGE_METRIC_IDS.map((id) => [id, true])
) as Record<UsageMetricId, boolean>

function getDateRange(days: number): { from: Date; to: Date } {
  const to = new Date()
  const from = new Date(to)
  from.setDate(from.getDate() - days)
  return { from, to }
}

export default function AdminUsagePage() {
  const [periodDays, setPeriodDays] = useState<number>(30)
  const [viewMode, setViewMode] = useState<ViewMode>('chart')
  const [visibleMetrics, setVisibleMetrics] = useState<Record<UsageMetricId, boolean>>(DEFAULT_VISIBLE_METRICS)
  const { from, to } = getDateRange(periodDays)
  const { data, isLoading, error } = useAdminUsageStats(from, to)

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" className="p-4">
        <p style={{ color: 'var(--text-secondary)' }}>Loading usage stats…</p>
      </div>
    )
  }

  if (error) {
    return (
      <section aria-labelledby="usage-error-heading">
        <h1 id="usage-error-heading" className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Usage & growth
        </h1>
        <p role="alert" style={{ color: 'var(--color-error)' }}>
          Failed to load usage stats: {error instanceof Error ? error.message : String(error)}.
        </p>
      </section>
    )
  }

  if (!data) {
    return (
      <section aria-labelledby="usage-empty-heading">
        <h1 id="usage-empty-heading" className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Usage & growth
        </h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          No usage data available or you do not have permission to view it.
        </p>
      </section>
    )
  }

  const avgCommentsPerThread =
    data.totalThreads > 0
      ? (data.totalComments / data.totalThreads).toFixed(1)
      : '0'

  return (
    <section aria-labelledby="usage-heading">
      <h1 id="usage-heading" className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
        Usage & growth
      </h1>

      <div className="mb-6">
        <label htmlFor="usage-period" className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
          Period
        </label>
        <select
          id="usage-period"
          value={periodDays}
          onChange={(e) => setPeriodDays(Number(e.target.value))}
          className="min-h-[44px] px-3 rounded border-2"
          style={{
            borderColor: 'var(--bg-tertiary)',
            color: 'var(--text-primary)',
            backgroundColor: 'var(--bg-primary)',
          }}
          aria-describedby="usage-period-desc"
        >
          {PERIOD_DAYS.map((d) => (
            <option key={d} value={d}>
              Last {d} days
            </option>
          ))}
        </select>
        <p id="usage-period-desc" className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
          Daily breakdown from {data.fromDate} to {data.toDate}.
        </p>
      </div>

      <section aria-labelledby="activity-summary-heading" className="mb-8">
        <h2 id="activity-summary-heading" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Activity summary
        </h2>
        <p style={{ color: 'var(--text-primary)' }}>
          Last {periodDays} days: <strong>{data.totalGroups.toLocaleString()} groups</strong>,{' '}
          <strong>{data.totalThreads.toLocaleString()} threads</strong>,{' '}
          <strong>{data.totalComments.toLocaleString()} comments</strong>,{' '}
          <strong>{data.totalArticles.toLocaleString()} articles</strong>,{' '}
          <strong>{data.totalArticleReads.toLocaleString()} articles read</strong>,{' '}
          <strong>{data.totalLinkFollows.toLocaleString()} links followed</strong>.
        </p>
        <p className="mt-2" style={{ color: 'var(--text-primary)' }}>
          Average comments per thread (in this period): <strong>{avgCommentsPerThread}</strong>.
        </p>
      </section>

      <section aria-labelledby="user-growth-heading" className="mb-8">
        <h2 id="user-growth-heading" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          User growth & activity by day
        </h2>
        <div className="mb-4" role="group" aria-labelledby="show-metrics-heading">
          <h3 id="show-metrics-heading" className="text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
            Show metrics
          </h3>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {USAGE_METRIC_IDS.map((id) => (
              <label key={id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={visibleMetrics[id]}
                  onChange={(e) =>
                    setVisibleMetrics((prev) => ({ ...prev, [id]: e.target.checked }))
                  }
                  className="rounded"
                  aria-describedby="show-metrics-desc"
                />
                <span style={{ color: 'var(--text-primary)' }}>{USAGE_METRIC_LABELS[id]}</span>
              </label>
            ))}
          </div>
          <p id="show-metrics-desc" className="sr-only">
            Toggle which metrics appear in the table and chart below.
          </p>
        </div>
        <div
          className="flex gap-2 mb-4"
          role="tablist"
          aria-label="View as table or chart"
        >
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'table'}
            aria-controls="usage-data-panel"
            id="usage-tab-table"
            onClick={() => setViewMode('table')}
            className="min-h-[44px] min-w-[44px] px-4 rounded border-2 font-medium"
            style={{
              borderColor: viewMode === 'table' ? 'var(--color-focus)' : 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              backgroundColor: viewMode === 'table' ? 'var(--bg-secondary)' : 'var(--bg-primary)',
            }}
          >
            Table
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === 'chart'}
            aria-controls="usage-data-panel"
            id="usage-tab-chart"
            onClick={() => setViewMode('chart')}
            className="min-h-[44px] min-w-[44px] px-4 rounded border-2 font-medium"
            style={{
              borderColor: viewMode === 'chart' ? 'var(--color-focus)' : 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              backgroundColor: viewMode === 'chart' ? 'var(--bg-secondary)' : 'var(--bg-primary)',
            }}
          >
            Chart
          </button>
        </div>
        <div
          id="usage-data-panel"
          role="tabpanel"
          aria-labelledby={viewMode === 'table' ? 'usage-tab-table' : 'usage-tab-chart'}
        >
          {viewMode === 'chart' ? (
            <UsageGrowthChart
              rows={data.rows}
              fromDate={data.fromDate}
              toDate={data.toDate}
              visibleMetrics={visibleMetrics}
            />
          ) : (
            <table
              className="w-full border-collapse"
              style={{ borderColor: 'var(--bg-tertiary)' }}
              summary={`Daily counts from ${data.fromDate} to ${data.toDate}. Columns: Date and selected metrics.`}
            >
              <thead>
                <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
                  <th scope="col" className="text-left p-2" style={{ color: 'var(--text-primary)' }}>
                    Date
                  </th>
                  {USAGE_METRIC_IDS.filter((id) => visibleMetrics[id]).map((id) => (
                    <th key={id} scope="col" className="text-right p-2" style={{ color: 'var(--text-primary)' }}>
                      {USAGE_METRIC_LABELS[id]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr>
                    <td colSpan={1 + USAGE_METRIC_IDS.filter((id) => visibleMetrics[id]).length} className="p-4 text-center" style={{ color: 'var(--text-secondary)' }}>
                      No data for this period.
                    </td>
                  </tr>
                ) : (
                  data.rows.map((row) => (
                    <tr key={row.stat_date} style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
                      <td className="p-2" style={{ color: 'var(--text-primary)' }}>
                        {format(new Date(row.stat_date), 'd MMM yyyy')}
                      </td>
                      {USAGE_METRIC_IDS.filter((id) => visibleMetrics[id]).map((id) => (
                        <td key={id} className="p-2 text-right" style={{ color: 'var(--text-primary)' }}>
                          {Number(row[USAGE_METRIC_ROW_KEYS[id]]).toLocaleString()}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </section>
  )
}
